import React, { useState } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { useHolidays, holidayOf } from "../../../hooks/useHolidays";
import { ServicePicker } from "../ServiceBadge";
import { hasService, SERVICES } from "../../../lib/services";

const empty = {
  client_nom: "", adresse: "", ville: "", client_email: "", date: "",
  prestation: "", service: "clean", collaborateurs: [], notes: "", notify_client: true,
};
const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand";

const sameCollab = (c, t) => (c.id && c.id === t.id) || (c.login && c.login === t.login) || (!c.id && !c.login && c.nom === t.nom);

const InterventionForm = ({ team, onSubmit, loading, initial, submitLabel = "Planifier" }) => {
  const [form, setForm] = useState({ ...empty, ...(initial || {}), service: (initial && initial.service) || "clean" });
  const holidays = useHolidays([form.date ? Number(form.date.slice(0, 4)) : new Date().getFullYear()]);
  const holiday = holidayOf(holidays, form.date);
  const eligible = team.filter((t) => hasService(t, form.service));
  const setService = (service) => setForm((f) => ({ ...f, service, collaborateurs: f.collaborateurs.filter((c) => team.some((t) => sameCollab(c, t) && hasService(t, service))) }));
  const toggleCollab = (t) => {
    setForm((f) => {
      const exists = f.collaborateurs.some((c) => sameCollab(c, t));
      const collaborateurs = exists
        ? f.collaborateurs.filter((c) => !sameCollab(c, t))
        : [...f.collaborateurs, { id: t.id, nom: t.nom, login: t.login }];
      return { ...f, collaborateurs };
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onSubmit(form);
    if (ok) setForm(empty);
  };

  return (
        <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-xl p-5 mb-5 grid sm:grid-cols-2 gap-3" data-testid="intervention-form">
          <input required placeholder="Nom du client *" className={inputCls} value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} data-testid="intv-client-input" />
          <input type="email" placeholder="Email du client (pour le suivi)" className={inputCls} value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="intv-email-input" />
          <input placeholder="Adresse" className={inputCls} value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
          <input placeholder="Ville" className={inputCls} value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
          <div>
            <input required type="datetime-local" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="intv-date-input" />
            {holiday && <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mt-1" data-testid="intv-holiday-warning"><AlertTriangle size={13} /> Jour férié : {holiday}</p>}
          </div>
          <input placeholder={form.service === "green" ? "Prestation (ex: Tonte + taille de haies)" : form.service === "handy" ? "Prestation (ex: Fixation d'étagères, 2 h)" : "Prestation (ex: Nettoyage bureaux)"} className={inputCls} value={form.prestation} onChange={(e) => setForm({ ...form, prestation: e.target.value })} />
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <label className="text-xs font-semibold text-neutral-600">Service</label>
            <ServicePicker value={form.service} onChange={setService} testid="intv-service" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Easers {SERVICES[form.service]?.short} assignés (équipe)</label>
            {eligible.length === 0 ? (
              <p className="text-xs text-neutral-400" data-testid="intv-collab-empty">Aucun easer « {SERVICES[form.service]?.label} ». Attribuez le tag Dolibarr « easer {form.service} » aux utilisateurs concernés puis synchronisez.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="intv-collab-list">
                {eligible.map((t) => {
                  const on = form.collaborateurs.some((c) => sameCollab(c, t));
                  return (
                    <button type="button" key={t.id} onClick={() => toggleCollab(t)} data-testid={`intv-collab-${t.id}`}
                      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-neutral-600 border-neutral-300 hover:border-brand"}`}>
                      {on && <CheckCircle2 size={14} />} {t.nom}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <input placeholder="Notes internes" className={`${inputCls} sm:col-span-2`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-neutral-700">
            <input type="checkbox" checked={form.notify_client} onChange={(e) => setForm({ ...form, notify_client: e.target.checked })} className="accent-brand w-4 h-4" data-testid="intv-notify-checkbox" />
            Envoyer au client l'email récapitulatif + le lien de suivi en fin de prestation
          </label>
          <button type="submit" disabled={loading} className="sm:col-span-2 bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md disabled:opacity-60" data-testid="intv-submit-btn">
            {loading ? "Enregistrement..." : submitLabel}
          </button>
        </form>
  );
};

export default InterventionForm;
